import * as React from 'react';
import { ITheme, IStyle, IStyleFunctionOrObject, styled, mergeStyles, concatStyleSetsWithProps } from '@fluentui/react';
import { ThemeColorPalette } from '@fluidx/component-ux';

export interface ChromeProps {
  /** The first slot to be filled by host */
  primarySpace?: React.ReactNode;

  /** The second slot to be filled by host */
  secondarySpace?: React.ReactNode;

  /** Call to provide customized styling */
  styles?: IStyleFunctionOrObject<ChromeStyleProps, ChromeStyles>;

  /** Theme (provided through customization.) */
  theme?: ITheme;
}

// Test ids for spaces.
export const chromeTestId = 'chromeTestId';
export const chromePrimarySpaceTestId = 'chromePrimarySpaceTestId';
export const chromeSecondarySpaceTestId = 'chromeSecondarySpaceTestId';

/**
 * Allows parent and sibling elements to give directional focus to the chrome
 */
export type FocusOnChrome = (forwardDirection: boolean) => void;

export interface ChromeStyles {
  /* Custom styling to be applied to the outer chrome element */
  root?: IStyle;

  /* Custom styling of the primary space in chrome  */
  primary?: IStyle;

  /* Custom styling of the secondary space in chrome */
  secondary?: IStyle;
}

export interface ChromeStyleProps extends Pick<ChromeProps, 'theme'> {}

/**
 * The UX bar above the component being displayed
 */
export const Chrome = React.memo(
  styled((props: ChromeProps) => {
    const { primarySpace, secondarySpace, styles } = props;
    const { root, primary, secondary } = concatStyleSetsWithProps(props, styles);

    return (
      <div className={mergeStyles(root)} data-testid={chromeTestId}>
        {primarySpace && (
          <div className={mergeStyles(primary)} data-testid={chromePrimarySpaceTestId}>
            {primarySpace}
          </div>
        )}
        {secondarySpace && (
          <div className={mergeStyles(secondary)} data-testid={chromeSecondarySpaceTestId}>
            {secondarySpace}
          </div>
        )}
      </div>
    );
  }, getDefaultStyle)
);

function getDefaultStyle({ theme }: ChromeStyleProps): ChromeStyles {
  const spaceLayoutStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center'
  };

  return {
    root: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '48px', // 20px of content & 14px of padding on top/bottom
      paddingLeft: '16px',
      paddingRight: '16px',
      fontSize: '14px',
      lineHeight: '20px',
      color: theme ? theme.palette.neutralSecondary : ThemeColorPalette.Neutral.neutralSecondary
    },
    primary: {
      ...spaceLayoutStyles,
      justifyContent: 'flex-start',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    secondary: {
      ...spaceLayoutStyles,
      justifyContent: 'flex-end'
    }
  };
}

// cspell:ignore uifabric testid
